import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { ArtifactChoice } from '@dreamai/types';
import { formatMoney, getFromPrice } from './hotelArtifact';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const PIN_PATH =
  'M 12,2 C 8.13,2 5,5.13 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.87 -3.13,-7 -7,-7 z';

const containerStyle: React.CSSProperties = { width: '100%', height: '100%' };

interface HotelArtifactMapProps {
  choices: ArtifactChoice[];
  activeId?: string;
  onSelect: (choiceId: string) => void;
}

interface Pin {
  id: string;
  title: string;
  position: google.maps.LatLngLiteral;
  price: string | null;
}

function pinIcon(active: boolean): google.maps.Symbol {
  return {
    path: PIN_PATH,
    fillColor: active ? '#489FD9' : '#1f1f1f',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 1.5,
    scale: active ? 1.9 : 1.5,
    anchor: new google.maps.Point(12, 22),
    labelOrigin: new google.maps.Point(12, -6),
  };
}

const HotelArtifactMap: React.FC<HotelArtifactMapProps> = ({
  choices,
  activeId,
  onSelect,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });
  const mapRef = useRef<google.maps.Map | null>(null);

  const pins = useMemo<Pin[]>(
    () =>
      choices.flatMap((c) => {
        if (c.lat == null || c.lng == null) return [];
        const from = getFromPrice(c);
        return [
          {
            id: c.choiceId,
            title: c.choiceTitle,
            position: { lat: c.lat, lng: c.lng },
            price:
              from != null && c.priceCurrency
                ? formatMoney(from, c.priceCurrency)
                : null,
          },
        ];
      }),
    [choices],
  );

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || pins.length === 0) return;
    if (pins.length === 1) {
      map.setCenter(pins[0].position);
      map.setZoom(14);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    pins.forEach((p) => bounds.extend(p.position));
    map.fitBounds(bounds, { top: 64, right: 48, bottom: 32, left: 48 });
  }, [pins]);

  useEffect(() => {
    if (isLoaded) fit();
  }, [isLoaded, fit]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      fit();
    },
    [fit],
  );

  if (!GOOGLE_MAPS_API_KEY || pins.length === 0) return null;

  return (
    <div className='hav-map'>
      {loadError ? (
        <div className='hav-map-placeholder'>Map unavailable</div>
      ) : !isLoaded ? (
        <div className='hav-map-placeholder'>Loading map…</div>
      ) : (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={pins[0].position}
          zoom={12}
          onLoad={onLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'cooperative',
            clickableIcons: false,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
            ],
          }}
        >
          {pins.map((pin) => {
            const active = pin.id === activeId;
            return (
              <Marker
                key={pin.id}
                position={pin.position}
                title={pin.title}
                zIndex={active ? 2 : 1}
                icon={pinIcon(active)}
                label={
                  pin.price
                    ? {
                        text: pin.price,
                        className: `hav-map-label${active ? ' active' : ''}`,
                        color: active ? '#489FD9' : '#1f1f1f',
                        fontSize: '12px',
                        fontWeight: '700',
                      }
                    : undefined
                }
                onClick={() => onSelect(pin.id)}
              />
            );
          })}
        </GoogleMap>
      )}
    </div>
  );
};

export default HotelArtifactMap;
